import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import BrandMark from "../components/BrandMark";

/**
 * /auth page — premium "Beit Midrash" entrance.
 *
 *   Visual layers (all CSS-only, prefers-reduced-motion respected):
 *     .auth-stage     → parchment background + slow gold ambient wash
 *     .auth-dust      → 14 floating gold-dust motes drifting upward
 *     .auth-card      → frosted-glass card with gold hairline + corners
 *     .auth-brand-halo→ soft gold glow behind the BrandMark
 *
 *   IMPORTANT: All auth LOGIC below this comment is preserved verbatim
 *   from the previous version of this file — Google OAuth flow, the
 *   email/password submit handler, `?next=` deep-link, referral-code
 *   cleanup, toasts, and navigate() targets. Only presentation changed.
 */
export default function Auth() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const dir = isEn ? "ltr" : "rtl";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  // Default landing page after a successful auth = the user's personal
  // Beit Midrash. Deep-links (e.g. /auth?next=/reader/Genesis/1) still
  // honored — restricted to internal paths to avoid open-redirect.
  const next = (() => {
    const raw = search.get("next");
    if (raw && raw.startsWith("/")) return raw;
    return "/beit-midrash";
  })();
  // Note: `?ref=CODE` capture is now handled globally by <ReferralCapture />
  // mounted at the App root, so it works on any landing route (home,
  // /library, /reader/...). No need to duplicate it here.
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success(t("auth.toast_login_success"));
        // Login: honor `?next=` deep-link, default to /beit-midrash.
        navigate(next, { replace: true });
      } else {
        if (password.length < 6) {
          toast.error(t("auth.toast_password_too_short"));
          setLoading(false);
          return;
        }
        await register(email, password, name);
        // Clear the stored referral code once it's been consumed by the
        // backend so subsequent registrations on the same device aren't
        // attributed to an old referrer.
        localStorage.removeItem("tanakh_referral_code");
        toast.success(t("auth.toast_register_success"));
        // First-time signup → run the 30-second welcome onboarding so the
        // user meets the 3 core tools (Basket / Notebook / Beit Midrash)
        // before landing in the library.
        navigate("/onboarding", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.toast_error_generic"));
    } finally {
      setLoading(false);
    }
  };

  /* Google OAuth handler — UNCHANGED. */
  const startGoogleAuth = async () => {
    try {
      const cfg = await api.googleOAuthConfig();
      if (!cfg?.client_id) {
        toast.error(t("auth.toast_google_not_configured"));
        return;
      }
      // CSRF state — random, single-use, kept in sessionStorage
      // so the callback page can verify it.
      const state =
        (window.crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random()}`).replace(/-/g, "");
      sessionStorage.setItem("google_oauth_state", state);
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
      // URLS, THIS BREAKS THE AUTH. Must match the URI registered in
      // Google Cloud Console authorized redirect URIs.
      const redirect = `${window.location.origin}/auth/google`;
      const url =
        "https://accounts.google.com/o/oauth2/v2/auth?" +
        new URLSearchParams({
          client_id: cfg.client_id,
          redirect_uri: redirect,
          response_type: "code",
          scope: "openid email profile",
          state,
          access_type: "online",
          prompt: "select_account",
          include_granted_scopes: "true",
        }).toString();
      window.location.href = url;
    } catch {
      toast.error(t("auth.toast_google_failed"));
    }
  };

  return (
    <div data-testid="auth-page-v2" className="relative min-h-screen w-full" dir={dir}>
      {/* Layer 1: parchment + ambient gold wash */}
      <div className="auth-stage" aria-hidden="true" />
      {/* Layer 2: floating gold-dust motes */}
      <div className="auth-dust" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, i) => (
          <span key={i} />
        ))}
      </div>

      {/* Layer 3: premium card centred over the stage */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-md items-center justify-center px-4 sm:px-6 py-12">
        <div
          className="auth-card auth-enter w-full px-6 sm:px-9 py-9 sm:py-11"
          data-testid="auth-page"
        >
          {/* Brand + verse header */}
          <div className="text-center space-y-3 mb-7">
            <div className="auth-enter auth-enter-d1 flex justify-center">
              <span className="auth-brand-halo">
                <BrandMark
                  size={68}
                  className="rounded-xl shadow-md ring-1 ring-gold/40"
                />
              </span>
            </div>
            <p
              className="auth-enter auth-enter-d2 font-latin italic text-[10px] tracking-[0.45em] uppercase text-gold/85"
              data-testid="auth-wordmark"
            >
              תוריא · Torahya
            </p>
            <h1
              className="auth-enter auth-enter-d3 font-heading text-3xl sm:text-4xl text-foreground leading-tight"
              data-testid="auth-heading"
            >
              {mode === "login" ? t("auth.welcome_back") : t("auth.welcome_new")}
            </h1>

            {/* Tehillim 19 — verse with nikud */}
            <div className="auth-enter auth-enter-d4 pt-2">
              <p
                dir="rtl"
                className="auth-verse font-heading text-lg sm:text-xl text-foreground/90 leading-relaxed"
                data-testid="auth-verse"
              >
                תּוֹרַת ה׳ תְּמִימָה מְשִׁיבַת נָפֶשׁ
              </p>
              <p className="font-latin italic text-[11px] tracking-[0.25em] uppercase text-gold/70 mt-1">
                תהילים י״ט
              </p>
            </div>
            <div className="auth-enter auth-enter-d4 ornamental-divider !my-4">✦</div>
            <p className="auth-enter auth-enter-d4 text-xs sm:text-sm text-muted-foreground max-w-xs mx-auto">
              {t("auth.subline")}
            </p>
          </div>

          {/* Tabs + forms */}
          <Tabs value={mode} onValueChange={setMode} dir={dir} className="auth-enter auth-enter-d5">
            <TabsList className="auth-tabs grid grid-cols-2 w-full bg-transparent border border-gold/30 rounded-md p-1 h-12">
              <TabsTrigger
                value="login"
                data-testid="tab-login"
                className="auth-tab font-heading data-[state=active]:bg-gold/15 data-[state=active]:text-gold rounded-sm transition-all"
              >
                {t("auth.tab_login")}
              </TabsTrigger>
              <TabsTrigger
                value="register"
                data-testid="tab-register"
                className="auth-tab font-heading data-[state=active]:bg-gold/15 data-[state=active]:text-gold rounded-sm transition-all"
              >
                {t("auth.tab_register")}
              </TabsTrigger>
            </TabsList>

            {/* Google OAuth — Custom (own consent screen branding) */}
            <div className="mt-6">
              <button
                type="button"
                data-testid="google-signin-btn"
                onClick={startGoogleAuth}
                className="auth-google-btn w-full flex items-center justify-center gap-3 rounded-sm py-3 font-heading text-foreground"
              >
                <svg className="h-5 w-5" viewBox="0 0 48 48">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                  <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
                </svg>
                {t("auth.google_continue")}
              </button>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-gold/25" />
                <span className="text-[11px] text-muted-foreground font-latin italic tracking-widest uppercase">{t("auth.or_divider")}</span>
                <div className="flex-1 h-px bg-gold/25" />
              </div>
            </div>

            <TabsContent value="login" className="mt-0 auth-enter auth-enter-d6">
              <form onSubmit={submit} className="space-y-4 auth-field" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="li-email" className="font-heading text-foreground/80">{t("auth.email_label")}</Label>
                  <Input
                    id="li-email"
                    data-testid="login-email"
                    type="email"
                    dir="ltr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="text-start"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="li-password" className="font-heading text-foreground/80">{t("auth.password_label")}</Label>
                  <Input
                    id="li-password"
                    data-testid="login-password"
                    type="password"
                    dir="ltr"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="text-start"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit"
                  className="sacred-btn w-full py-6 mt-2"
                >
                  {loading ? t("auth.login_cta_busy") : t("auth.login_cta")}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-0 auth-enter auth-enter-d6">
              <form onSubmit={submit} className="space-y-4 auth-field" data-testid="register-form">
                <div className="space-y-2">
                  <Label htmlFor="ri-name" className="font-heading text-foreground/80">{t("auth.name_label")}</Label>
                  <Input
                    id="ri-name"
                    data-testid="register-name"
                    dir={dir}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={isEn ? "text-left" : "text-right"}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ri-email" className="font-heading text-foreground/80">{t("auth.email_label")}</Label>
                  <Input
                    id="ri-email"
                    data-testid="register-email"
                    type="email"
                    dir="ltr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="text-start"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ri-password" className="font-heading text-foreground/80">{t("auth.password_label_register")}</Label>
                  <Input
                    id="ri-password"
                    data-testid="register-password"
                    type="password"
                    dir="ltr"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="text-start"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit"
                  className="sacred-btn w-full py-6 mt-2"
                >
                  {loading ? t("auth.register_cta_busy") : t("auth.register_cta")}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
